export enum PoolStyle {
  IN_GROUND = 'in_ground',
  ABOVE_GROUND = 'above_ground',
}

export const PoolStyleDisplay: Record<PoolStyle, string> = {
  [PoolStyle.IN_GROUND]: 'In-ground',
  [PoolStyle.ABOVE_GROUND]: 'Above-ground',
};

export enum PoolType {
  CHLORINE = 'chlorine',
  SALT = 'salt',
}

export const PoolTypeDisplay: Record<PoolType, string> = {
  [PoolType.CHLORINE]: 'Chlorine',
  [PoolType.SALT]: 'Salt',
};

// Reason codes for deactivating a pool.
export enum DeactivationReason {
  CHURNED = 'churned',
  SEASONAL_PAUSE = 'seasonal-pause',
  CONTRACT_ENDED = 'contract-ended',
  OTHER = 'other',
}

export const DeactivationReasonDisplay: Record<DeactivationReason, string> = {
  [DeactivationReason.CHURNED]: 'Customer churned',
  [DeactivationReason.SEASONAL_PAUSE]: 'Seasonal pause',
  [DeactivationReason.CONTRACT_ENDED]: 'Contract ended',
  [DeactivationReason.OTHER]: 'Other',
};

export const DEACTIVATION_REASON_OTHER_TEXT_MAX_LENGTH = 60;

export type BillingModelType = 'all-inclusive' | 'pay-per-visit';

export interface AllInclusiveBillingModel {
  type: 'all-inclusive';
  monthlyRate: number; // $ per month
}

export interface PayPerVisitBillingModel {
  type: 'pay-per-visit';
  cleaningFee: number; // $ per cleaning visit
  maintenanceFee: number; // $ per maintenance visit
}

export type BillingModel = AllInclusiveBillingModel | PayPerVisitBillingModel;

export const BillingModelTypeDisplay: Record<BillingModelType, string> = {
  'all-inclusive': 'All-Inclusive',
  'pay-per-visit': 'Pay Per Visit',
};

export const BillingModelDisplay = (billingModel: BillingModel): string =>
  BillingModelTypeDisplay[billingModel.type];

export type PoolCustomer = {
  id: string;
  name: string;
};

export type Pool = {
  id: string;
  poolName: string;
  poolStyle: PoolStyle;
  poolType: PoolType;
  poolVolume: number;
  poolSize: string;
  poolStreetAddress: string;
  poolCity: string;
  poolState: string;

  // customer relationship
  customer: PoolCustomer;

  // Billing configuration
  billingModel: BillingModel;

  // Defaults to true on creation
  isActive: boolean;
  deactivatedAt?: Date | null;
  deactivationReason?: DeactivationReason | null;
  deactivationReasonText?: string | null;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy: string;
};

// Denormalised pool data for caching in the pool list page. This is a subset of the full Pool type.
export type PoolCacheEntry = {
  id: string;
  poolName: string;
  poolAddress: string;
  poolStyle: string;
  poolType: string;
  poolSize: string | null;
  poolVolume: number | null;
  customerName: string;
  customerId: string;
  isActive: boolean;
  billingModelType: BillingModelType;
};

export type PoolCache = {
  totalPools: number;
  poolsList: PoolCacheEntry[];
  lastUpdated: Date | null;
};

export type DeactivatePoolRequest = {
  companyId: string;
  poolId: string;
  deactivationReason: DeactivationReason;
  deactivationReasonText?: string;
};

export type ReactivatePoolRequest = {
  companyId: string;
  poolId: string;
};

// ---- Pool service (for chemical readings / service history on the detail page) ----

export enum ServiceType {
  CLEANING = 'cleaning',
  MAINTENANCE = 'maintenance',
  CUSTOM = 'custom',
}

export type PoolInfo = {
  id: string;
  name: string;
};

export type ServiceUser = {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'technician';
};

export type ChemicalUsageEntry = {
  amount: number;
  unitPrice: number;
};

export type PoolService = {
  id: string;
  testResults: { [key: string]: number };
  chemicalUsage: { [chemicalId: string]: ChemicalUsageEntry };
  pool: PoolInfo;
  serviceDate: Date;
  serviceNotes?: string;
  companyId: string;
  servicedBy: ServiceUser;
  createdBy: ServiceUser;
  updatedBy?: ServiceUser;
  createdAt: Date;
  updatedAt?: Date;
};
